<template>
  <Transition name="fade">
    <div
      v-if="visible"
      @click="() => closeOnClickModal && handleClose()"
      class="confirm-dialog-container"
    >
      <div
        @click.stop
        class="confirm-box"
      >
        <img
          @click="handleClose"
          class="close-image"
          src="@/assets/images/close-icon.svg"
          alt=""
        >

        <div class="title">
          {{ title }}
        </div>
        <div class="content">
          {{ content }}
        </div>

        <div class="footer">
          <div
            @click="handleCancel"
            class="cancel-button"
          >
            {{ cancelText }}
          </div>
          <div
            @click="handleConfirm"
            class="confirm-button"
          >
            {{ confirmText }}
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Title'
  },
  content: {
    type: String,
    default: 'Content'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  closeOnClickModal: {
    type: Boolean,
    default: true
  },
  closeOnPressEscape: {
    type: Boolean,
    default: true
  }
})

const emits = defineEmits(['confirm', 'cancel'])

const visible = defineModel({ required: true })

const handleClose = () => {
  visible.value = false
}

const handleCancel = () => {
  emits('cancel')
  handleClose()
}

const handleConfirm = () => {
  emits('confirm')
  handleClose()
}

const handleKeydown = (e) => {
  if (e.key === 'Escape' && visible.value && props.closeOnPressEscape) {
    handleClose()
  }
}

// 监听 visible 变化
watch(visible, (newVal) => {
  if (newVal) {
    // 对话框打开时
    document.addEventListener('keydown', handleKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    // 对话框关闭时
    document.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
  }
}, { immediate: true })

onMounted(() => {
  visible.value = true
})

// 组件卸载时清理
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<style lang='less'>
.confirm-dialog-container {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .64);
  backdrop-filter: blur(4px);
  z-index: 999;

  .confirm-box {
    position: relative;
    width: 400px;
    height: 172px;
    border-radius: 8px;
    background-color: white;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    .close-image {
      position: absolute;
      top: 16px;
      right: 24px;
      cursor: pointer;
    }

    .title {
      margin-top: 32px;
      margin-left: 24px;
      color: #232323;
      font-size: 16px;
      font-family: mon-600;
      line-height: 20px;
    }

    .content {
      margin-top: 16px;
      margin-left: 24px;
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
      line-height: 16px;
    }

    .footer {
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      background-color: #f8f8f8;

      .cancel-button, .confirm-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 160px;
        height: 32px;
        border-radius: 20px;
        font-size: 14px;
        font-family: mon-600;
        cursor: pointer;
      }

      .cancel-button {
        color: #232323;
        background-color: white;
        border: 1px solid var(--dark-split-color);
      }

      .confirm-button {
        color: white;
        background-color: #052784;
      }
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
